<script setup lang="ts">
import { stateShortNames } from '@/utils/PortalConstants';
import TableFooterBar from '@/components/bars/TableFooterBar.vue';
import KBtn from '@/components/btns/KBtn.vue';
import AccordionWithCheckboxesCustomTable from '@/components/customTables/AccordionWithCheckboxesCustomTable.vue';
import SimpleLoader from '@/components/loaders/SimpleLoader.vue';
import WeeklyReportSearchForm from '@/components/searchForms/WeeklyReportSearchForm.vue';
import ReturnLabelsDownloadVMenu from '@/components/vMenus/ReturnLabelsDownloadVMenu.vue';
import FormWrapper from '@/components/wrappers/FormWrapper.vue';
import PageContentWrapper from '@/components/wrappers/PageContentWrapper.vue';
import useCommonRouteQuery from '@/composables/routeQueries/useCommonRouteQuery';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useRunSheetReportView from '@/composables/views/useRunSheetReportView';

const { getNextFiveDaysRange } = useCurrentDateString();
const { queryDates, queryStates } = useCommonRouteQuery({
  queryDates: getNextFiveDaysRange(),
  queryStates: stateShortNames,
});

const {
  search,
  initialData,
  isFetchingData,
  isFetchingFileDownload,
  isFetchingReturnLabels,
  isActionDisabled,
  tableData,
  handleDriverSelected,
  handleDaySelected,
  handleDownloadRunSheetReport,
  handleDownloadReturnLabels,
  handlePrintReturnLabels,
} = useRunSheetReportView(queryDates, queryStates);
</script>

<template>
  <div :class="$style.runSheetReportView" data-testid="runSheetReportView">
    <PageContentWrapper>
      <FormWrapper :class="$style.formWrapper">
        <WeeklyReportSearchForm
          :initial-data="initialData"
          :loading="isFetchingData"
          :class="$style.form"
          @submit="search"
        />
      </FormWrapper>
      <template v-if="isFetchingData">
        <SimpleLoader :size="64" :width="6" class="mx-auto my-20" />
      </template>
      <div v-else :class="$style.runSheetReports">
        <AccordionWithCheckboxesCustomTable
          v-model="tableData"
          @handle-driver-selected="handleDriverSelected"
          @handle-day-selected="handleDaySelected"
        />
      </div>
    </PageContentWrapper>
    <TableFooterBar>
      <menu :class="$style.footerMenuList">
        <li :class="$style.footerMenuListItem">
          <KBtn
            size="lg"
            append-icon="mdi-arrow-down"
            :disabled="isActionDisabled"
            :loading="isFetchingFileDownload"
            @click="handleDownloadRunSheetReport"
          >
            Download report
          </KBtn>
        </li>
        <li :class="$style.footerMenuListItem">
          <ReturnLabelsDownloadVMenu
            :disabled="isActionDisabled"
            :loading="isFetchingReturnLabels"
            @download-click="handleDownloadReturnLabels"
          />
        </li>
        <li :class="$style.footerMenuListItem">
          <KBtn
            size="lg"
            append-icon="mdi-printer-outline"
            :disabled="isActionDisabled"
            @click="handlePrintReturnLabels"
          >
            Print return labels
          </KBtn>
        </li>
      </menu>
    </TableFooterBar>
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.runSheetReportView {
  .formWrapper {
    padding: 2.4rem 1.5rem 0.5rem 1.5rem;
    max-width: variables.$tableWrapperWidth;
    margin: 2rem 0;
  }
  .runSheetReports {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }
}
.footerMenuList {
  list-style: none;
  margin: 0;
  padding: 0;
}
  .footerMenuListItem {
    display: inline;
  }
  .footerMenuListItem + .footerMenuListItem {
    margin-left: 1rem;
  }
</style>
@/composables/useRunSheetReportView
